import * as React from "react";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import { DayPicker, useDayPicker, useNavigation, type CaptionProps } from "react-day-picker";
import { addYears, isAfter, isBefore, startOfMonth } from "date-fns";

import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button-variants";

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

const navButtonClass = cn(
  buttonVariants({ variant: "outline" }),
  "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 disabled:pointer-events-none disabled:opacity-30",
);

/**
 * Custom caption that adds previous/next *year* jumps alongside the default
 * previous/next month controls, so users can move a whole year at a time
 * instead of clicking through twelve months. Laid out symmetrically — year +
 * month controls flanking a centred month/year label — so the row stays aligned
 * and the same height as the default caption (which keeps the calendar anchored;
 * see the fixedWeeks note below). Every control carries an explicit aria-label
 * and the label announces changes via aria-live.
 */
function CalendarCaption({ displayMonth }: CaptionProps) {
  const { currentMonth, goToMonth, previousMonth, nextMonth } = useNavigation();
  const { locale, fromDate, toDate, formatters } = useDayPicker();

  const month = startOfMonth(currentMonth);
  const fromMonth = fromDate ? startOfMonth(fromDate) : undefined;
  const toMonth = toDate ? startOfMonth(toDate) : undefined;

  // Year jumps respect the same bounds as month nav: clamp the target into
  // [fromDate, toDate] and disable the control once we're already at the edge.
  const prevYearDisabled = fromMonth ? !isAfter(month, fromMonth) : false;
  const nextYearDisabled = toMonth ? !isBefore(month, toMonth) : false;

  const goToYear = (delta: number) => {
    let target = startOfMonth(addYears(month, delta));
    if (fromMonth && isBefore(target, fromMonth)) target = fromMonth;
    if (toMonth && isAfter(target, toMonth)) target = toMonth;
    goToMonth(target);
  };

  return (
    <div className="flex items-center justify-between px-1 pt-1">
      <div className="flex items-center space-x-1">
        <button
          type="button"
          aria-label="Go to previous year"
          className={navButtonClass}
          disabled={prevYearDisabled}
          onClick={() => goToYear(-1)}
        >
          <ChevronsLeft className="h-4 w-4" />
        </button>
        <button
          type="button"
          aria-label="Go to previous month"
          className={navButtonClass}
          disabled={!previousMonth}
          onClick={() => previousMonth && goToMonth(previousMonth)}
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
      </div>

      <div aria-live="polite" role="presentation" className="text-sm font-medium">
        {formatters.formatCaption(displayMonth, { locale })}
      </div>

      <div className="flex items-center space-x-1">
        <button
          type="button"
          aria-label="Go to next month"
          className={navButtonClass}
          disabled={!nextMonth}
          onClick={() => nextMonth && goToMonth(nextMonth)}
        >
          <ChevronRight className="h-4 w-4" />
        </button>
        <button
          type="button"
          aria-label="Go to next year"
          className={navButtonClass}
          disabled={nextYearDisabled}
          onClick={() => goToYear(1)}
        >
          <ChevronsRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

function Calendar({ className, classNames, showOutsideDays = true, fixedWeeks = true, ...props }: CalendarProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      // Always render 6 week rows so every month is the same height. Without this,
      // months span 4–6 weeks and the calendar grows/shrinks and jumps up/down as
      // you navigate between them (Discord issue 8).
      fixedWeeks={fixedWeeks}
      className={cn("p-3", className)}
      classNames={{
        months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
        month: "space-y-4",
        caption: "flex justify-center pt-1 relative items-center",
        caption_label: "text-sm font-medium",
        nav: "space-x-1 flex items-center",
        nav_button: cn(
          buttonVariants({ variant: "outline" }),
          "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100",
        ),
        nav_button_previous: "absolute left-1",
        nav_button_next: "absolute right-1",
        table: "w-full border-collapse space-y-1",
        head_row: "flex",
        head_cell: "text-muted-foreground rounded-md w-9 font-normal text-[0.8rem]",
        row: "flex w-full mt-2",
        cell: "h-9 w-9 text-center text-sm p-0 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20",
        day: cn(buttonVariants({ variant: "ghost" }), "h-9 w-9 p-0 font-normal aria-selected:opacity-100"),
        day_range_end: "day-range-end",
        day_selected:
          "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
        day_today: "bg-accent text-accent-foreground",
        day_outside:
          "day-outside text-muted-foreground opacity-50 aria-selected:bg-accent/50 aria-selected:text-muted-foreground aria-selected:opacity-30",
        day_disabled: "text-muted-foreground opacity-50",
        day_range_middle: "aria-selected:bg-accent aria-selected:text-accent-foreground",
        day_hidden: "invisible",
        ...classNames,
      }}
      components={{
        Caption: CalendarCaption,
        IconLeft: ({ ..._props }) => <ChevronLeft className="h-4 w-4" />,
        IconRight: ({ ..._props }) => <ChevronRight className="h-4 w-4" />,
      }}
      {...props}
    />
  );
}
Calendar.displayName = "Calendar";

export { Calendar };
